import React, { useState, useEffect } from 'react';
import { Euro, Save, TrendingUp, Plane, Mountain, Building, Moon, Info } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const PricingConfig = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    base_price: 30,
    price_per_km: 1.50,
    minimum_price: 50,
    night_multiplier: 1.30,
    airport_multiplier: 1.20,
    mountain_multiplier: 1.15,
    city_multiplier: 1.10
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pricing-config`);
      const data = await response.json();
      setConfig(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching config:', error);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pricing-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        toast.success('Configurazione prezzi salvata con successo!');
      } else {
        toast.error('Errore nel salvataggio');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig({ ...config, [field]: parseFloat(value) });
  };

  const formatPercent = (value) => {
    return `+${Math.round((value - 1) * 100)}%`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Example calculation
  const exampleDistance = 120;
  const examplePrice = config.base_price + (exampleDistance * config.price_per_km);
  const exampleAirport = Math.round(examplePrice * config.airport_multiplier);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-black text-white py-8 px-4">
        <div className="container mx-auto">
          <h1 className="text-4xl font-bold mb-2">Configurazione Prezzi</h1>
          <p className="text-gray-400">Gestisci i prezzi e i moltiplicatori per le prenotazioni</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Info Box */}
        <Card className="p-6 bg-blue-50 border-blue-200 mb-8">
          <div className="flex items-start gap-3">
            <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-blue-900 mb-2">Come Funziona il Sistema Prezzi</h3>
              <p className="text-sm text-blue-800 mb-2">
                Il prezzo finale viene calcolato con questa formula:
              </p>
              <p className="text-sm font-mono bg-white p-2 rounded text-blue-900">
                Prezzo = (Base + Distanza KM × Prezzo/KM) × Moltiplicatore
              </p>
              <p className="text-xs text-blue-700 mt-2">
                Il sistema applica automaticamente i moltiplicatori in base al tipo di destinazione e orario.
              </p>
            </div>
          </div>
        </Card>

        {/* Example Calculation */}
        <Card className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 mb-8">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-amber-600" />
            Esempio di Calcolo
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Torino Airport → Milano ({exampleDistance}km):</span>
              <span className="font-semibold">€{Math.round(examplePrice)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Con maggiorazione aeroporto ({formatPercent(config.airport_multiplier)}):</span>
              <span className="font-bold text-amber-600">€{exampleAirport}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Con maggiorazione notturna ({formatPercent(config.night_multiplier)}):</span>
              <span className="font-bold text-purple-600">€{Math.round(exampleAirport * config.night_multiplier)}</span>
            </div>
          </div>
        </Card>

        {/* Configuration Form */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Base Prices */}
          <Card className="p-6">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Euro className="w-5 h-5 text-amber-600" />
              Prezzi Base
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Prezzo Base (€)
                </label>
                <input
                  type="number"
                  value={config.base_price}
                  onChange={(e) => handleChange('base_price', e.target.value)}
                  step="5"
                  min="0"
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:outline-none text-gray-800 font-semibold"
                />
                <p className="text-xs text-gray-500 mt-1">Prezzo fisso iniziale per ogni corsa</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Prezzo per KM (€/km)
                </label>
                <input
                  type="number"
                  value={config.price_per_km}
                  onChange={(e) => handleChange('price_per_km', e.target.value)}
                  step="0.10"
                  min="0"
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:outline-none text-gray-800 font-semibold"
                />
                <p className="text-xs text-gray-500 mt-1">Tariffa applicata per ogni chilometro</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Prezzo Minimo (€)
                </label>
                <input
                  type="number"
                  value={config.minimum_price}
                  onChange={(e) => handleChange('minimum_price', e.target.value)}
                  step="5"
                  min="0"
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:outline-none text-gray-800 font-semibold"
                />
                <p className="text-xs text-gray-500 mt-1">Prezzo minimo garantito per ogni corsa</p>
              </div>
            </div>
          </Card>

          {/* Multipliers */}
          <Card className="p-6">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Moltiplicatori
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Plane className="w-4 h-4 text-blue-600" />
                  Aeroporto ({formatPercent(config.airport_multiplier)})
                </label>
                <input
                  type="number"
                  value={config.airport_multiplier}
                  onChange={(e) => handleChange('airport_multiplier', e.target.value)}
                  step="0.05"
                  min="1"
                  max="2"
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-blue-500 focus:outline-none text-gray-800 font-semibold"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Mountain className="w-4 h-4 text-green-600" />
                  Montagna ({formatPercent(config.mountain_multiplier)})
                </label>
                <input
                  type="number"
                  value={config.mountain_multiplier}
                  onChange={(e) => handleChange('mountain_multiplier', e.target.value)}
                  step="0.05"
                  min="1"
                  max="2"
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-green-500 focus:outline-none text-gray-800 font-semibold"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Building className="w-4 h-4 text-orange-600" />
                  City Center ({formatPercent(config.city_multiplier)})
                </label>
                <input
                  type="number"
                  value={config.city_multiplier}
                  onChange={(e) => handleChange('city_multiplier', e.target.value)}
                  step="0.05"
                  min="1"
                  max="2"
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-500 focus:outline-none text-gray-800 font-semibold"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Moon className="w-4 h-4 text-purple-600" />
                  Notturno 22:00-06:00 ({formatPercent(config.night_multiplier)})
                </label>
                <input
                  type="number"
                  value={config.night_multiplier}
                  onChange={(e) => handleChange('night_multiplier', e.target.value)}
                  step="0.05"
                  min="1"
                  max="2"
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-purple-500 focus:outline-none text-gray-800 font-semibold"
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <Button
            onClick={() => window.location.href = '/admin'}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold px-8 py-3"
          >
            Torna alla Dashboard
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold px-8 py-3 shadow-lg"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva Configurazione'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PricingConfig;
